import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Separator } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';
import { ParentSize } from '@visx/responsive';

import { BalanceProjectionChartLegend } from './components/chart/ProjectionChartLegend';
import { ForecasterLink } from './components/ForecasterLink';
import { ProjectionHeader } from './components/header/ProjectionHeader';
import { ProjectionHighlights } from './components/highlights/ProjectionHighlights';
import { ProjectionLoadingState } from './components/loading-state/ProjectionLoadingState';
import { ProjectionZeroState } from './components/zero-state/ProjectionZeroState';
import { PurposeProjectionDocument } from './graphql/purposeProjection.generated';

import { ProjectionDisclosures } from '../../../forecaster/shared/components/ProjectionDisclosures';
import { ProjectionChart } from '../../../Shared/components-v2/charts/projection-chart/ProjectionChart';
import { MinimalFeeWarning } from '../../../Shared/components-v2/minimal-fee-warning/MinimalFeeWarning';

const ProjectionCardView: React.FC = () => {
  const purposeId = useRequiredParam('purposeId');
  const { data } = useGraphQLQuery(PurposeProjectionDocument, { variables: { id: purposeId }, throwOnError: true });

  if (!data) return <ProjectionLoadingState />;

  const { purpose, disclosures } = data;

  const { timeSeries, target } = (() => {
    switch (purpose.__typename) {
      case 'PurposeMajorPurchase':
        return {
          timeSeries: purpose.majorPurchaseProjection?.balanceTimeSeries,
          target: purpose.majorPurchaseTarget?.amount?.cents,
        };
      case 'PurposeEmergencyFund':
        return {
          timeSeries: purpose.emergencyFundProjection?.balanceTimeSeries,
          target: purpose.emergencyFundTarget?.amount.cents,
        };
      case 'PurposeRetirementGrowth':
        return { timeSeries: purpose.retirementGrowthProjection?.balanceTimeSeries };
      case 'PurposeRetirementIncome':
        return { timeSeries: purpose.retirementIncomeProjection?.balanceTimeSeries };
      default:
        return purpose;
    }
  })();

  if (!timeSeries) return null;

  const isZeroState = timeSeries.every(point => point.expected?.cents === 0);

  if (isZeroState) {
    return <ProjectionZeroState purpose={purpose} disclosures={disclosures} />;
  }

  const managedAccount = purpose.envelope?.accounts?.find(account => account.__typename === 'ManagedAccount');

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      <Card.Header css={{ display: 'flex', flexDirection: 'column', gap: 'space.50' }}>
        <H2 variant="title200">Goal progress</H2>
        <ProjectionHeader purpose={purpose} />
      </Card.Header>
      <Separator />
      {managedAccount?.minFeeWarning && <MinimalFeeWarning minFeeWarning={managedAccount.minFeeWarning} />}
      <Card.Content css={{ display: 'flex', flexDirection: 'column', gap: 'space.200', position: 'relative' }}>
        <Box
          css={{ display: 'flex', flexDirection: 'column', gap: 'space.100', alignItems: 'start', minWidth: 'size.0' }}
        >
          <BalanceProjectionChartLegend />
          <ParentSize>
            {({ width }) => (
              <ProjectionChart
                data={timeSeries}
                width={width}
                target={target}
                showLeftAxis={false}
                height={150}
                margin={{ top: 0, right: 1, bottom: 24, left: 1 }}
              />
            )}
          </ParentSize>
        </Box>
        <ProjectionHighlights purpose={purpose} />
        <ForecasterLink purpose={purpose} />
        <ProjectionDisclosures disclosures={disclosures} />
      </Card.Content>
    </Card>
  );
};

export const ProjectionCard: React.FC = () => (
  <ErrorBoundary fallbackType="none">
    <ProjectionCardView />
  </ErrorBoundary>
);
